/**
 * Views/Prenomina/ModalResetearLineas.jsx
 *
 * Resincroniza las líneas de la prenómina abierta contra el padrón de empleados activos, con una
 * previsualización antes de tocar nada: quién se va, quién entra y qué se conserva y por qué.
 *
 * El servidor decide TODO: los motivos, sus etiquetas y colores, y qué renglones vienen
 * palomeados. Aquí no se reimplementa ninguna regla — así agregar un motivo nuevo en
 * Utils/resetLineasPrenomina.utils.js lo pinta bien sin tocar este archivo.
 *
 * Las tablas son <Table> de antd y no TableItem: TableItem fuerza `scroll` y `pagination` propios.
 */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { Table, Tag, Space, Alert, Tabs, Checkbox, Tooltip, Modal, message } from 'antd'
import { WarningOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons'
import { ModalItem, ButtonItem } from '../../../../Components/Items'

const FASE = { CARGANDO: 'cargando', PREVIEW: 'preview', APLICANDO: 'aplicando', RESUMEN: 'resumen' }

const dinero = (n) =>
  (n || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })

const codigoTexto = (c) => (c == null ? '—' : String(c).padStart(4, '0'))

class ModalResetearLineas extends Component {

  state = {
    fase: FASE.CARGANDO,
    preview: null,
    quitarSel: [],
    agregarSel: [],
    calcularAlTerminar: false,
    resumen: null,
    // Se prende cuando el aplicar rebota porque el padrón se movió: el modal vuelve a PREVIEW con
    // los datos frescos y hay que decirle al usuario por qué cambió lo que estaba viendo.
    padronCambio: false,
    error: null,
    // `disableWhenClick` de ButtonItem sólo se rearma cuando cambia `resetKey`. Sin esto, un
    // aplicar que falla o que rebota por firma vieja dejaría el botón muerto hasta cerrar el modal.
    intentos: 0,
  }

  componentDidMount() {
    if (this.props.visible) this.cargarPreview()
  }

  componentDidUpdate(prev) {
    if (this.props.visible && !prev.visible) this.cargarPreview()
  }

  /** Deja el estado listo a partir de una respuesta de preview (venga del preview o del rebote). */
  aplicarPreviewAlEstado(preview, extra = {}) {
    this.setState(prev => ({
      fase: FASE.PREVIEW,
      preview,
      quitarSel:  preview.aQuitar.filter(r => r.preseleccionada).map(r => r.empleadoId),
      agregarSel: preview.aAgregar.filter(r => r.preseleccionada).map(r => r.empleadoId),
      calcularAlTerminar: preview.aAgregar.some(r => r.preseleccionada),
      error: null,
      intentos: prev.intentos + 1,
      ...extra,
    }))
  }

  /** Regresa a la previsualización sin perderla, rearmando el botón de aplicar. */
  volverAPreview() {
    this.setState(prev => ({ fase: FASE.PREVIEW, intentos: prev.intentos + 1 }))
  }

  async cargarPreview() {
    const { empresaId, prenominaId, headersToken } = this.props
    this.setState({ fase: FASE.CARGANDO, padronCambio: false, error: null })
    try {
      const res = await axios.post(
        'prenomina/previsualizarResetLineas',
        { empresaId, prenominaId },
        { headers: headersToken }
      )
      if (res.data.success !== 2) {
        this.setState({ fase: FASE.PREVIEW, preview: null, error: res.data.message || 'No se pudo comparar contra el padrón' })
        return
      }
      this.aplicarPreviewAlEstado(res.data)
    } catch (err) {
      this.setState({ fase: FASE.PREVIEW, preview: null, error: 'No se pudo comparar contra el padrón' })
    }
  }

  /** Confirmación extra sólo cuando hay captura manual de por medio: eso no se recupera. */
  confirmarYAplicar() {
    const { preview, quitarSel } = this.state
    const conAviso = preview.aQuitar.filter(r => quitarSel.includes(r.empleadoId) && r.advertencias.length > 0)

    if (conAviso.length === 0) return this.aplicar()

    Modal.confirm({
      title: '¿Quitar líneas con captura manual?',
      width: 560,
      okText: 'Quitar de todos modos',
      okType: 'danger',
      cancelText: 'Revisar',
      content: (
        <div>
          <p>
            Se van a borrar <b>{quitarSel.length}</b> línea(s), <b>{conAviso.length}</b> de ellas con trabajo
            capturado a mano. Esto no se puede deshacer.
          </p>
          <ul style={{ maxHeight: 200, overflowY: 'auto', paddingLeft: 18 }}>
            {conAviso.slice(0, 15).map(r => (
              <li key={r.empleadoId} style={{ fontSize: 12 }}>
                {codigoTexto(r.codigo)} · {r.nombreCompleto} — {r.advertencias.join(' · ')}
              </li>
            ))}
          </ul>
          {conAviso.length > 15 && <p style={{ fontSize: 12, color: '#888' }}>y {conAviso.length - 15} más…</p>}
        </div>
      ),
      onOk: () => this.aplicar(),
      // El botón ya se deshabilitó al hacer clic (disableWhenClick); si el usuario se arrepiente
      // aquí, hay que rearmarlo o se queda muerto.
      onCancel: () => this.volverAPreview(),
    })
  }

  async aplicar() {
    const { empresaId, prenominaId, headersToken } = this.props
    const { preview, quitarSel, agregarSel, calcularAlTerminar } = this.state

    this.setState({ fase: FASE.APLICANDO })
    try {
      const res = await axios.post(
        'prenomina/aplicarResetLineas',
        {
          empresaId, prenominaId,
          firma: preview.firma,
          quitarEmpleadoIds: quitarSel,
          agregarEmpleadoIds: agregarSel,
        },
        { headers: headersToken }
      )

      // El padrón se movió entre la previsualización y el aplicar: no se escribió nada y el
      // servidor manda el resumen nuevo.
      if (res.data.success === 1 && res.data.preview) {
        this.aplicarPreviewAlEstado(res.data.preview, { padronCambio: true })
        return
      }
      if (res.data.success !== 2) {
        message.error(res.data.message || 'No se pudo aplicar')
        this.volverAPreview()
        return
      }

      this.setState({ fase: FASE.RESUMEN, resumen: res.data.resumen })
      this.props.onAplicado({
        quitadas: res.data.resumen.quitadas,
        agregadas: res.data.resumen.agregadas,
        calcular: calcularAlTerminar,
      })
    } catch (err) {
      message.error('No se pudo aplicar')
      this.volverAPreview()
    }
  }

  columnasComunes() {
    return [
      {
        title: 'Cód.', dataIndex: 'codigo', width: 78,
        render: (_, r) => (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontVariantNumeric: 'tabular-nums' }}>{codigoTexto(r.codigo)}</div>
            {r.serieNumeroEmpleado && <div style={{ fontSize: 10, color: '#aaa' }}>{r.serieNumeroEmpleado}</div>}
          </div>
        ),
      },
      {
        title: 'Empleado', dataIndex: 'nombreCompleto',
        render: (_, r) => (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 13 }}>{r.nombreCompleto || '—'}</div>
            {r.rfc && <div style={{ fontSize: 10, color: '#aaa' }}>{r.rfc}</div>}
          </div>
        ),
      },
      {
        title: 'Motivo', dataIndex: 'motivo', width: 190,
        render: (_, r) => (
          <Tooltip title={r.motivoTexto} mouseEnterDelay={0}>
            <Tag color={r.motivoColor} style={{ cursor: 'help' }}>{r.motivoEtiqueta}</Tag>
          </Tooltip>
        ),
      },
    ]
  }

  render() {
    const { visible, onClose } = this.props
    const { fase, preview, quitarSel, agregarSel, calcularAlTerminar, resumen, padronCambio, error } = this.state

    const conteos = preview?.conteos
    const bloqueado = (preview?.bloqueos?.length || 0) > 0
    const nadaSeleccionado = quitarSel.length === 0 && agregarSel.length === 0

    const colQuitar = [
      ...this.columnasComunes(),
      {
        title: 'Neto actual', dataIndex: 'totalNeto', width: 110, align: 'right',
        render: (v) => <span style={{ color: v ? '#cf1322' : '#bbb' }}>{dinero(v)}</span>,
      },
      {
        title: '', dataIndex: 'advertencias', width: 40, align: 'center',
        render: (a) => (a && a.length > 0
          ? <Tooltip title={a.join(' · ')} mouseEnterDelay={0}><WarningOutlined style={{ color: '#faad14' }} /></Tooltip>
          : null),
      },
    ]

    const colConservadas = [
      ...this.columnasComunes(),
      {
        title: 'Neto actual', dataIndex: 'totalNeto', width: 110, align: 'right',
        render: (v) => dinero(v),
      },
    ]

    return (
      <ModalItem
        title="Resetear líneas contra los empleados activos"
        visible={visible}
        spinning={fase === FASE.CARGANDO}
        onCancel={onClose}
        footer={null}
        width={900}
      >
        {fase === FASE.RESUMEN && resumen ? (
          <div style={{ paddingTop: 8 }}>
            <Alert
              type={resumen.omitidas.length > 0 ? 'warning' : 'success'}
              showIcon
              message={`Listo: ${resumen.quitadas} línea(s) quitada(s), ${resumen.agregadas} agregada(s).`}
              description={`La prenómina quedó con ${resumen.totalLineasFinal} empleado(s).`}
            />
            {resumen.omitidas.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>No se pudieron aplicar:</div>
                <ul style={{ maxHeight: 220, overflowY: 'auto', paddingLeft: 18 }}>
                  {resumen.omitidas.map((o, i) => (
                    <li key={i} style={{ fontSize: 12 }}>
                      {o.nombreCompleto ? <b>{o.nombreCompleto}</b> : null} {o.motivo}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <ButtonItem onClick={onClose}>Cerrar</ButtonItem>
            </div>
          </div>
        ) : error ? (
          <div style={{ paddingTop: 8 }}>
            <Alert type="error" showIcon message={error} />
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <ButtonItem blanco onClick={onClose}>Cerrar</ButtonItem>
                <ButtonItem azul icon={<ReloadOutlined />} onClick={() => this.cargarPreview()}>Reintentar</ButtonItem>
              </Space>
            </div>
          </div>
        ) : !preview ? null : (
          <div style={{ paddingTop: 8 }}>

            {padronCambio && (
              <Alert
                type="warning" showIcon style={{ marginBottom: 10 }}
                message="La nómina cambió desde que abriste esta ventana"
                description="Alguien movió el padrón o recalculó la nómina. No se aplicó nada: este es el resumen actualizado, revísalo antes de continuar."
              />
            )}

            <Alert
              type="info" showIcon style={{ marginBottom: 10 }}
              message={`Se compara con los empleados activos del periodo «${preview.prenomina.tipoPeriodoNombre}».`}
              description="Las líneas de quien sigue trabajando no se tocan: conservan sus cálculos, importes capturados e incidencias."
            />

            {preview.bloqueos.map((b, i) => (
              <Alert key={i} type="error" showIcon style={{ marginBottom: 10 }} message={b} />
            ))}
            {preview.avisos.map((a, i) => (
              <Alert key={i} type="warning" showIcon style={{ marginBottom: 10 }} message={a} />
            ))}

            <Space wrap style={{ marginBottom: 10 }}>
              <Tag color="error">{conteos.aQuitar} se pueden quitar</Tag>
              <Tag color="success">{conteos.aAgregar} se pueden agregar</Tag>
              <Tag color="default">{conteos.conservadasActivas} sin cambios</Tag>
              <Tag color="processing">{conteos.conservadasProtegidas} conservadas por su situación</Tag>
              {conteos.conAdvertencia > 0 && (
                <Tag color="warning" icon={<WarningOutlined />}>{conteos.conAdvertencia} con captura manual</Tag>
              )}
            </Space>

            <Tabs size="small" defaultActiveKey={conteos.aQuitar > 0 ? 'quitar' : 'agregar'}>
              <Tabs.TabPane tab={`Se quitan (${quitarSel.length}/${conteos.aQuitar})`} key="quitar">
                <Table
                  size="small"
                  rowKey="empleadoId"
                  columns={colQuitar}
                  dataSource={preview.aQuitar}
                  pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
                  scroll={{ y: 260 }}
                  locale={{ emptyText: 'Ninguna línea sobra: todos los empleados de la prenómina siguen aplicando.' }}
                  rowSelection={{
                    selectedRowKeys: quitarSel,
                    onChange: (keys) => this.setState({ quitarSel: keys }),
                  }}
                />
              </Tabs.TabPane>

              <Tabs.TabPane tab={`Se agregan (${agregarSel.length}/${conteos.aAgregar})`} key="agregar">
                <Table
                  size="small"
                  rowKey="empleadoId"
                  columns={this.columnasComunes()}
                  dataSource={preview.aAgregar}
                  pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
                  scroll={{ y: 260 }}
                  locale={{ emptyText: 'No falta ningún empleado activo.' }}
                  rowSelection={{
                    selectedRowKeys: agregarSel,
                    onChange: (keys) => this.setState({ agregarSel: keys }),
                  }}
                />
              </Tabs.TabPane>

              <Tabs.TabPane tab={`Se conservan (${conteos.conservadasProtegidas})`} key="conservan">
                <Table
                  size="small"
                  rowKey="empleadoId"
                  columns={colConservadas}
                  dataSource={preview.conservadas}
                  pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
                  scroll={{ y: 260 }}
                  locale={{ emptyText: 'Ninguna línea necesita conservarse por un motivo especial.' }}
                />
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>
                  Aquí sólo salen las que se conservan por un motivo que conviene revisar. Además hay{' '}
                  <b>{conteos.conservadasActivas}</b> empleado(s) activo(s) que siguen igual, sin cambios.
                </div>
              </Tabs.TabPane>
            </Tabs>

            <div style={{ marginTop: 10 }}>
              <Checkbox
                checked={calcularAlTerminar}
                disabled={agregarSel.length === 0}
                onChange={(e) => this.setState({ calcularAlTerminar: e.target.checked })}
              >
                Calcular la nómina al terminar
              </Checkbox>
              <div style={{ fontSize: 11, color: '#aaa', marginLeft: 24 }}>
                Las líneas nuevas nacen en Pendiente y salen en ceros hasta que se calculen.
              </div>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0',
            }}>
              <span style={{ fontSize: 12, color: '#888' }}>
                La prenómina quedaría con{' '}
                <b>{conteos.totalLineas - quitarSel.length + agregarSel.length}</b> empleado(s).
              </span>
              <Space>
                <ButtonItem blanco onClick={onClose}>Cancelar</ButtonItem>
                <ButtonItem blanco icon={<ReloadOutlined />} onClick={() => this.cargarPreview()}>
                  Volver a comparar
                </ButtonItem>
                <ButtonItem
                  rojo={quitarSel.length > 0}
                  azul={quitarSel.length === 0}
                  icon={<SyncOutlined />}
                  disabled={bloqueado || nadaSeleccionado}
                  loading={fase === FASE.APLICANDO}
                  disableWhenClick
                  resetKey={this.state.intentos}
                  onClick={() => this.confirmarYAplicar()}
                >
                  Aplicar — quitar {quitarSel.length}, agregar {agregarSel.length}
                </ButtonItem>
              </Space>
            </div>
          </div>
        )}
      </ModalItem>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
})

export default connect(mapStateToProps)(ModalResetearLineas)
